/* Created by Interactive 63 - https://interactive63.com */
/**
 * The Layers panel: a searchable, keyboard-navigable tree of the SVG's elements. Selection,
 * renaming, visibility and export toggles all go through store actions. Long lists are
 * virtualised so thousands of elements stay responsive.
 */
import type { ElementId } from '../../core/model/types';
import {
  renameElement,
  selectElements,
  setExpanded,
  setHovered,
  toggleElementExcluded,
  toggleElementHidden,
} from '../store/actions';
import {
  deleteSelectedLayers,
  duplicateSelectedLayers,
  flattenSelectedTransforms,
  groupSelectedLayers,
  moveLayers,
  moveOutOfGroup,
  nudgeLayers,
  reportArrange,
  splitSelectedPaths,
  ungroupSelectedLayers,
} from '../store/layer-actions';
import { Component } from '../ui/component';
import { h } from '../ui/dom';
import { icon, iconForTag } from '../ui/icons';
import { toast } from '../ui/toast';
import {
  ancestorsOf,
  buildRows,
  GRADIENTS_ID,
  rangeBetween,
  resolveDrop,
  type DropResult,
  type LayerRow,
} from './layers-rows';
import { addStopAfterSelected, deleteSelectedStops } from '../store/stop-actions';
import { showContextMenu } from './timeline/context-menu';

const ROW_HEIGHT = 26;
const VIRTUALISE_ABOVE = 500;
const OVERSCAN = 10;
/** How far the pointer must travel before a press on a row becomes a drag. */
const DRAG_THRESHOLD_PX = 4;
/** Distance from the top or bottom edge of the tree where dragging scrolls it, and how fast. */
const AUTOSCROLL_EDGE_PX = 28;
const AUTOSCROLL_STEP_PX = 12;

/** A press on a row that may turn into a drag. */
interface DragState {
  id: ElementId;
  pointerId: number;
  startX: number;
  startY: number;
  active: boolean;
  /** The layers being moved: the whole selection when the pressed row is part of it. */
  ids: ElementId[];
  clientY: number;
  drop: DropResult | null;
  frame: number;
}

export class LayersPanel extends Component {
  private tree!: HTMLElement;
  private rows: LayerRow[] = [];
  private query = '';
  private activeId: string | null = null;
  private anchorId: string | null = null;
  private renamingId: string | null = null;
  private revealId: string | null = null;
  private trackedKey = '';
  private drag: DragState | null = null;
  /** Set just after a drag ends so the click that follows it does not change the selection. */
  private suppressClick = false;

  /** Creates the panel section. */
  protected override createRoot(): HTMLElement {
    return h('section', { class: ['panel', 'panel-layers'], 'aria-label': 'Layers' });
  }

  /** Builds the search box and tree and subscribes to the store. */
  protected override init(): void {
    const search = h('input', {
      type: 'search',
      class: 'layers-search',
      placeholder: 'Search layers',
      'aria-label': 'Search layers',
    });
    search.addEventListener('input', () => {
      this.query = search.value;
      this.render();
    });

    this.tree = h('div', {
      class: 'layers-tree',
      role: 'tree',
      'aria-label': 'Layers',
      'aria-multiselectable': 'true',
      tabindex: 0,
    });
    this.el.append(
      h('h2', null, 'Layers'),
      h('div', { class: 'layers-search-wrap' }, search),
      this.tree,
    );

    this.listen(this.tree, 'click', (event) => {
      this.onClick(event);
    });
    this.listen(this.tree, 'keydown', (event) => {
      this.onKeyDown(event);
    });
    this.listen(this.tree, 'pointerdown', (event) => {
      this.onPointerDown(event);
    });
    this.listen(this.tree, 'pointermove', (event) => {
      this.onPointerMove(event);
    });
    this.listen(this.tree, 'pointerup', () => {
      this.onPointerUp();
    });
    this.listen(this.tree, 'pointercancel', () => {
      this.endDrag();
    });
    this.listen(this.tree, 'contextmenu', (event) => {
      this.onContextMenu(event);
    });
    this.listen(this.tree, 'scroll', () => {
      if (this.rows.length > VIRTUALISE_ABOVE) this.paint();
    });
    this.listen(this.tree, 'pointerover', (event) => {
      const id = this.rowId(event.target);
      setHovered(this.store, id && id !== GRADIENTS_ID ? id : null);
    });
    this.listen(this.tree, 'pointerleave', () => {
      setHovered(this.store, null);
    });

    this.select(
      (s) =>
        [
          s.project.elements,
          s.project.rootIds,
          s.editor.selection.elementIds,
          s.editor.expandedElementIds,
        ] as const,
      (_value, prev) => {
        this.noteSelectionChange(prev[2]);
        this.render();
      },
      { equality: 'shallow' },
    );
    this.select(
      (s) =>
        Array.from(new Set(s.project.tracks.map((t) => t.elementId)))
          .sort()
          .join(','),
      (key) => {
        this.trackedKey = key;
        this.render();
      },
      { immediate: true },
    );
  }

  /** Expands ancestors of a newly selected element and marks it to be scrolled into view. */
  private noteSelectionChange(previous: readonly ElementId[]): void {
    const { project, editor } = this.store.getState();
    const selected = editor.selection.elementIds;
    const added = selected.filter((id) => !previous.includes(id));
    const target = added[added.length - 1];
    if (target === undefined) return;
    this.revealId = target;
    this.activeId = target;
    const closed = ancestorsOf(project.elements, target).filter(
      (id) => !editor.expandedElementIds.includes(id),
    );
    if (
      project.elements[target]?.tag === 'stop' &&
      !editor.expandedElementIds.includes(GRADIENTS_ID)
    ) {
      closed.push(GRADIENTS_ID);
    }
    if (closed.length > 0) setExpanded(this.store, closed, true);
  }

  /** Recomputes the rows from the store and repaints. */
  private render(): void {
    const { project, editor } = this.store.getState();
    this.rows = buildRows(project, new Set(editor.expandedElementIds), this.query);
    this.paint();
    if (this.revealId !== null) {
      const index = this.rows.findIndex((r) => r.id === this.revealId);
      if (index !== -1) this.scrollToIndex(index);
      this.revealId = null;
    }
  }

  /** Scrolls the tree so a row is visible. */
  private scrollToIndex(index: number): void {
    const top = index * ROW_HEIGHT;
    const view = this.tree.clientHeight;
    if (top < this.tree.scrollTop) this.tree.scrollTop = top;
    else if (view > 0 && top + ROW_HEIGHT > this.tree.scrollTop + view) {
      this.tree.scrollTop = top + ROW_HEIGHT - view;
    }
    if (this.rows.length > VIRTUALISE_ABOVE) this.paint();
  }

  /** Renders the visible slice of rows into the tree. */
  private paint(): void {
    const { editor } = this.store.getState();
    const selected = new Set(editor.selection.elementIds);
    const tracked = new Set(this.trackedKey === '' ? [] : this.trackedKey.split(','));

    let start = 0;
    let end = this.rows.length;
    if (this.rows.length > VIRTUALISE_ABOVE) {
      const visible = Math.ceil((this.tree.clientHeight || 600) / ROW_HEIGHT);
      start = Math.max(0, Math.floor(this.tree.scrollTop / ROW_HEIGHT) - OVERSCAN);
      end = Math.min(this.rows.length, start + visible + OVERSCAN * 2);
    }

    const children: HTMLElement[] = [];
    if (start > 0)
      children.push(
        h('div', { class: 'layers-spacer', style: { height: `${start * ROW_HEIGHT}px` } }),
      );
    for (const row of this.rows.slice(start, end)) {
      children.push(this.buildRow(row, selected.has(row.id), tracked.has(row.id)));
    }
    if (end < this.rows.length) {
      children.push(
        h('div', {
          class: 'layers-spacer',
          style: { height: `${(this.rows.length - end) * ROW_HEIGHT}px` },
        }),
      );
    }
    if (this.rows.length === 0) {
      children.push(
        h(
          'p',
          { class: 'panel-empty' },
          this.query ? 'No layers match your search.' : 'Import an SVG to see its layers here.',
        ),
      );
    }
    // An empty tree has no items, so it is only a tree once there are rows to navigate.
    const hasRows = this.rows.length > 0;
    this.tree.setAttribute('role', hasRows ? 'tree' : 'group');
    if (hasRows) this.tree.setAttribute('aria-multiselectable', 'true');
    else {
      this.tree.removeAttribute('aria-multiselectable');
      this.tree.removeAttribute('aria-activedescendant');
    }
    this.tree.replaceChildren(...children);
    if (hasRows && this.activeId)
      this.tree.setAttribute('aria-activedescendant', `layer-${this.activeId}`);

    const input = this.tree.querySelector<HTMLInputElement>('.layer-rename');
    if (input && document.activeElement !== input) {
      input.focus();
      input.select();
    }
  }

  /** Builds one tree row. */
  private buildRow(row: LayerRow, selected: boolean, tracked: boolean): HTMLElement {
    const element = row.element;
    const isGroup = row.id === GRADIENTS_ID;
    const label = isGroup ? 'Gradients' : (element?.label ?? row.id);

    const labelNode =
      this.renamingId === row.id
        ? this.buildRenameInput(row.id, label)
        : h('span', { class: 'layer-label', title: label }, label);

    const toggle = h(
      'span',
      {
        class: ['layer-toggle', row.expanded ? 'is-open' : '', row.hasChildren ? '' : 'is-leaf'],
        'data-action': 'toggle',
      },
      icon('chevron'),
    );

    const actions = element
      ? h(
          'span',
          { class: 'layer-actions' },
          h(
            'button',
            {
              type: 'button',
              tabindex: -1,
              class: element.hiddenInEditor ? 'is-on' : '',
              'data-action': 'hide',
              'aria-label': element.hiddenInEditor ? 'Show layer' : 'Hide layer',
              title: element.hiddenInEditor ? 'Show layer' : 'Hide layer',
            },
            icon(element.hiddenInEditor ? 'eye-off' : 'eye'),
          ),
          h(
            'button',
            {
              type: 'button',
              tabindex: -1,
              class: element.excludeFromExport ? 'is-on' : '',
              'data-action': 'exclude',
              'aria-label': 'Exclude from export',
              'aria-pressed': String(element.excludeFromExport),
              title: 'Exclude from export',
            },
            icon(element.excludeFromExport ? 'export-off' : 'export'),
          ),
        )
      : null;

    return h(
      'div',
      {
        class: [
          'layer-row',
          selected ? 'is-selected' : '',
          this.activeId === row.id ? 'is-active' : '',
        ],
        id: `layer-${row.id}`,
        role: 'treeitem',
        'aria-level': row.depth + 1,
        'aria-selected': isGroup ? null : String(selected),
        'aria-expanded': row.hasChildren ? String(row.expanded) : null,
        'data-id': row.id,
        style: { height: `${ROW_HEIGHT}px`, paddingLeft: `${row.depth * 14 + 4}px` },
      },
      toggle,
      icon(isGroup ? 'group' : iconForTag(element?.tag ?? 'g')),
      labelNode,
      tracked ? h('span', { class: 'layer-dot', title: 'Has animation' }) : null,
      actions,
    );
  }

  /** Builds the inline rename field. Enter or blur saves; Escape cancels. */
  private buildRenameInput(id: ElementId, label: string): HTMLInputElement {
    const input = h('input', { class: 'layer-rename', value: label, 'aria-label': 'Layer name' });
    let finished = false;
    /** Ends renaming, saving the new name when requested. */
    const finish = (save: boolean): void => {
      if (finished) return;
      finished = true;
      this.renamingId = null;
      if (save) renameElement(this.store, id, input.value);
      this.render();
      this.tree.focus();
    };
    input.addEventListener('keydown', (event) => {
      event.stopPropagation();
      if (event.key === 'Enter') finish(true);
      else if (event.key === 'Escape') finish(false);
    });
    input.addEventListener('blur', () => {
      finish(true);
    });
    input.addEventListener('click', (event) => {
      event.stopPropagation();
    });
    return input;
  }

  /** Begins inline renaming for a row. */
  private startRename(id: ElementId): void {
    this.renamingId = id;
    this.render();
  }

  /** Returns the row ID containing an event target. */
  private rowId(target: EventTarget | null): string | null {
    return target instanceof Element
      ? (target.closest('.layer-row')?.getAttribute('data-id') ?? null)
      : null;
  }

  /** Handles clicks on rows, toggles and action buttons. */
  private onClick(event: MouseEvent): void {
    if (this.suppressClick) return;
    const id = this.rowId(event.target);
    if (!id || !(event.target instanceof Element)) return;
    const action = event.target.closest('[data-action]')?.getAttribute('data-action');
    this.activeId = id;

    if (action === 'hide' && id !== GRADIENTS_ID) {
      toggleElementHidden(this.store, id);
      return;
    }
    if (action === 'exclude' && id !== GRADIENTS_ID) {
      toggleElementExcluded(this.store, id);
      return;
    }
    // The tree re-renders on the first click, so a native dblclick never fires; use click count.
    if (event.detail === 2 && event.target.closest('.layer-label') && id !== GRADIENTS_ID) {
      this.startRename(id);
      return;
    }
    const row = this.rows.find((r) => r.id === id);
    if (action === 'toggle' || id === GRADIENTS_ID) {
      if (row?.hasChildren) setExpanded(this.store, [id], !row.expanded);
      return;
    }

    const current = this.store.getState().editor.selection.elementIds;
    if (event.shiftKey && this.anchorId) {
      selectElements(this.store, rangeBetween(this.rows, this.anchorId, id));
      return;
    }
    this.anchorId = id;
    if (event.ctrlKey || event.metaKey) {
      selectElements(this.store, [id], 'toggle');
    } else if (current.length !== 1 || current[0] !== id) {
      selectElements(this.store, [id]);
    } else {
      this.render();
    }
  }

  /** Starts tracking a press on a row, which becomes a drag once the pointer has moved far enough. */
  private onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || !(event.target instanceof Element)) return;
    if (event.target.closest('[data-action], .layer-rename')) return;
    const id = this.rowId(event.target);
    if (!id || id === GRADIENTS_ID || this.renamingId !== null) return;
    if (this.store.getState().project.elements[id]?.tag === 'stop') return;
    this.drag = {
      id,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
      ids: [],
      clientY: event.clientY,
      drop: null,
      frame: 0,
    };
  }

  /** Begins the drag once the pointer has travelled, then keeps the drop indicator up to date. */
  private onPointerMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (!drag.active) {
      const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
      if (distance < DRAG_THRESHOLD_PX) return;
      this.beginDrag(drag);
    }
    drag.clientY = event.clientY;
    this.updateDrop(drag);
  }

  /** Marks the dragged rows and starts the loop that scrolls the tree near its edges. */
  private beginDrag(drag: DragState): void {
    const { project, editor } = this.store.getState();
    const selected = editor.selection.elementIds;
    const pool = selected.includes(drag.id) ? selected : [drag.id];
    const chosen = new Set(pool.filter((id) => project.elements[id]?.tag !== 'stop'));
    drag.ids = [...chosen].filter(
      (id) => !ancestorsOf(project.elements, id).some((ancestor) => chosen.has(ancestor)),
    );
    drag.active = true;
    this.tree.setPointerCapture(drag.pointerId);
    this.tree.classList.add('is-dragging');
    for (const id of drag.ids) this.rowElement(id)?.classList.add('is-drag-source');
    const scroll = (): void => {
      const rect = this.tree.getBoundingClientRect();
      if (drag.clientY < rect.top + AUTOSCROLL_EDGE_PX) this.tree.scrollTop -= AUTOSCROLL_STEP_PX;
      else if (drag.clientY > rect.bottom - AUTOSCROLL_EDGE_PX) {
        this.tree.scrollTop += AUTOSCROLL_STEP_PX;
      }
      this.updateDrop(drag);
      drag.frame = requestAnimationFrame(scroll);
    };
    drag.frame = requestAnimationFrame(scroll);
  }

  /** The row element for a layer, if it is currently drawn. */
  private rowElement(id: string): HTMLElement | null {
    return this.tree.querySelector<HTMLElement>(`[data-id="${id}"]`);
  }

  /** Works out where the layers would land at the pointer and shows it. */
  private updateDrop(drag: DragState): void {
    const rect = this.tree.getBoundingClientRect();
    const y = drag.clientY - rect.top + this.tree.scrollTop;
    const rowIndex = Math.floor(y / ROW_HEIGHT);
    const fraction = (y - rowIndex * ROW_HEIGHT) / ROW_HEIGHT;
    drag.drop = resolveDrop(this.store.getState().project, this.rows, rowIndex, fraction, drag.ids);
    this.clearDropMarks();
    this.tree.classList.toggle('is-no-drop', drag.drop === null);
    if (drag.drop) this.rowElement(drag.drop.rowId)?.classList.add(`drop-${drag.drop.position}`);
  }

  /** Removes the drop indicator from every row. */
  private clearDropMarks(): void {
    for (const row of this.tree.querySelectorAll('.layer-row')) {
      row.classList.remove('drop-before', 'drop-after', 'drop-inside');
    }
  }

  /** Stops tracking the pointer and clears the drag styling, without moving anything. */
  private endDrag(): void {
    const drag = this.drag;
    this.drag = null;
    if (!drag?.active) return;
    cancelAnimationFrame(drag.frame);
    if (this.tree.hasPointerCapture(drag.pointerId))
      this.tree.releasePointerCapture(drag.pointerId);
    this.tree.classList.remove('is-dragging', 'is-no-drop');
    this.clearDropMarks();
    for (const row of this.tree.querySelectorAll('.is-drag-source')) {
      row.classList.remove('is-drag-source');
    }
  }

  /** Drops the layers where the indicator shows, and swallows the click that follows. */
  private onPointerUp(): void {
    const drag = this.drag;
    this.endDrag();
    if (!drag?.active) return;
    this.suppressClick = true;
    setTimeout(() => {
      this.suppressClick = false;
    }, 0);
    if (drag.drop) reportArrange(moveLayers(this.store, drag.ids, drag.drop.target));
  }

  /** Opens the layer menu for the row under the pointer, selecting it first if it was not. */
  private onContextMenu(event: MouseEvent): void {
    const id = this.rowId(event.target);
    if (!id || id === GRADIENTS_ID) return;
    event.preventDefault();
    this.openLayerMenu(id, event.clientX, event.clientY);
  }

  /** Shows Rename, Duplicate and Delete for a row; the latter two act on the whole selection. */
  private openLayerMenu(id: ElementId, x: number, y: number): void {
    this.activeId = id;
    if (!this.store.getState().editor.selection.elementIds.includes(id)) {
      this.anchorId = id;
      selectElements(this.store, [id]);
    }
    const count = this.store.getState().editor.selection.elementIds.length;
    const plural = count > 1 ? 's' : '';
    if (this.store.getState().project.elements[id]?.tag === 'stop') {
      showContextMenu(x, y, [
        {
          label: 'Rename',
          disabled: count > 1,
          onSelect: () => {
            this.startRename(id);
          },
        },
        { separator: true },
        {
          label: 'Add stop after',
          disabled: count > 1,
          onSelect: () => {
            reportArrange(addStopAfterSelected(this.store));
          },
        },
        {
          label: `Delete stop${plural}`,
          onSelect: () => {
            reportArrange(deleteSelectedStops(this.store));
          },
        },
      ]);
      return;
    }
    showContextMenu(x, y, [
      {
        label: 'Rename',
        disabled: count > 1,
        onSelect: () => {
          this.startRename(id);
        },
      },
      {
        label: `Duplicate layer${plural}`,
        onSelect: () => {
          duplicateSelectedLayers(this.store);
        },
      },
      { separator: true },
      {
        label: 'Split into parts',
        onSelect: () => {
          reportArrange(splitSelectedPaths(this.store));
        },
      },
      {
        label: 'Flatten transform',
        onSelect: () => {
          reportArrange(flattenSelectedTransforms(this.store));
        },
      },
      { separator: true },
      {
        label: 'Group (Ctrl + G)',
        onSelect: () => {
          reportArrange(groupSelectedLayers(this.store));
        },
      },
      {
        label: 'Ungroup (Ctrl + Shift + G)',
        onSelect: () => {
          reportArrange(ungroupSelectedLayers(this.store));
        },
      },
      {
        label: 'Move up (Alt + Up)',
        onSelect: () => {
          reportArrange(nudgeLayers(this.store, this.selectedIds(), 'up'));
        },
      },
      {
        label: 'Move down (Alt + Down)',
        onSelect: () => {
          reportArrange(nudgeLayers(this.store, this.selectedIds(), 'down'));
        },
      },
      {
        label: 'Move out of group (Alt + Left)',
        onSelect: () => {
          reportArrange(moveOutOfGroup(this.store, this.selectedIds()));
        },
      },
      { separator: true },
      {
        label: `Delete layer${plural}`,
        onSelect: () => {
          if (deleteSelectedLayers(this.store) > 0) {
            toast.info(`Layer${plural} deleted. Undo brings ${count > 1 ? 'them' : 'it'} back.`);
          }
        },
      },
    ]);
  }

  /** The selected elements. */
  private selectedIds(): ElementId[] {
    return [...this.store.getState().editor.selection.elementIds];
  }

  /** Alt + Up, Down and Left rearrange the selected layers. Returns whether the key was used. */
  private handleArrangeKey(event: KeyboardEvent): boolean {
    if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return false;
    const ids = this.selectedIds();
    switch (event.key) {
      case 'ArrowUp':
        reportArrange(nudgeLayers(this.store, ids, 'up'));
        return true;
      case 'ArrowDown':
        reportArrange(nudgeLayers(this.store, ids, 'down'));
        return true;
      case 'ArrowLeft':
        reportArrange(moveOutOfGroup(this.store, ids));
        return true;
      default:
        return false;
    }
  }

  /** Implements the ARIA tree keyboard pattern. */
  private onKeyDown(event: KeyboardEvent): void {
    if (this.drag?.active && event.key === 'Escape') {
      event.preventDefault();
      this.endDrag();
      return;
    }
    if (this.handleArrangeKey(event)) {
      event.preventDefault();
      return;
    }
    const index = this.rows.findIndex((r) => r.id === this.activeId);
    const row = this.rows[index];
    // The menu key and Shift + F10 open the layer menu beside the active row.
    if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) {
      event.preventDefault();
      const rect = row?.element
        ? document.getElementById(`layer-${row.id}`)?.getBoundingClientRect()
        : undefined;
      if (row && rect) this.openLayerMenu(row.id, rect.left + rect.width / 2, rect.bottom);
      return;
    }
    /** Moves the active row by an offset, clamped to the list. */
    const move = (to: number): void => {
      const next = this.rows[Math.max(0, Math.min(this.rows.length - 1, to))];
      if (!next) return;
      this.activeId = next.id;
      this.revealId = next.id;
      this.render();
    };

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        move(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        move(index === -1 ? 0 : index - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (row?.hasChildren && !row.expanded) setExpanded(this.store, [row.id], true);
        else if (row?.expanded) move(index + 1);
        break;
      case 'ArrowLeft': {
        event.preventDefault();
        if (row?.expanded) setExpanded(this.store, [row.id], false);
        else if (row?.element?.parentId)
          move(this.rows.findIndex((r) => r.id === row.element?.parentId));
        break;
      }
      case 'Enter':
        event.preventDefault();
        if (!row) break;
        if (row.id === GRADIENTS_ID) setExpanded(this.store, [row.id], !row.expanded);
        else {
          this.anchorId = row.id;
          selectElements(this.store, [row.id]);
        }
        break;
      case 'F2':
        event.preventDefault();
        if (row?.element) this.startRename(row.id);
        break;
    }
  }
}
